import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {DataService} from '../data.service';
import {LoggerService} from '../../shared/common-components/logger.service';
import {BehaviorSubject, combineLatest, Observable, of, Subject, takeWhile} from 'rxjs';

import {
  IFFShipmentSummaryRequest,
  IFFShipmentSummaryResponse,
  PageFilter,
  PageRequest,
  PageResponse,
  PageSort
} from 'src/app/shared/common-components/common-structure';
import {catchError, debounceTime, switchMap} from 'rxjs/operators';
import {DownloadFileResponse} from 'src/app/shared/common-components/file-storage/downloadFileResponse';
import AppConstants from 'src/app/shared/common-components/AppConstants';
import {CarrierService} from "../carrier/carrier.service";
import {UserService} from "../user/user.service";
import {HhgCarrierRateFilingService} from "../../features/hhg-carrier-rate-filing/hhg-carrier-rate-filing.service";

@Injectable({
  providedIn: 'root'
})
export class IffShipmentSummaryService extends DataService {

  private baseURI = this.apiUri + '/iffMasterList';
  private iffMasterXlsxURI = this.publicApiUri + '/iffMasterListxlsx';
  private paygovURI = this.apiUri + '/paygov/getToken';
  private paygovCompleteURI = this.apiUri + '/paygov/completeTransaction/';
  private _logger: LoggerService;

  private HHGShipmentSummaryRequest = new BehaviorSubject<IFFShipmentSummaryRequest>(new IFFShipmentSummaryRequest('HHG', 'HHG'));
  private SITShipmentSummaryRequest = new BehaviorSubject<IFFShipmentSummaryRequest>(new IFFShipmentSummaryRequest('HHG', 'SIT'));
  private FRTShipmentSummaryRequest = new BehaviorSubject<IFFShipmentSummaryRequest>(new IFFShipmentSummaryRequest('FRT', 'FRT'));


  private HHGShipmentPaginationRequest = new BehaviorSubject<PageRequest<IFFShipmentSummaryRequest>>(new PageRequest<IFFShipmentSummaryRequest>(1, 0, [], []));
  private SITShipmentPaginationRequest = new BehaviorSubject<PageRequest<IFFShipmentSummaryRequest>>(new PageRequest<IFFShipmentSummaryRequest>(1, 0, [], []));
  private FRTShipmentPaginationRequest = new BehaviorSubject<PageRequest<IFFShipmentSummaryRequest>>(new PageRequest<IFFShipmentSummaryRequest>(1, 0, [], []));

  private SelectedHHGShipmentSummary = new BehaviorSubject<IFFShipmentSummaryResponse>({} as IFFShipmentSummaryResponse);
  private SelectedFRTShipmentSummary = new BehaviorSubject<IFFShipmentSummaryResponse>({} as IFFShipmentSummaryResponse);

  HHGShipmentMasterResults$ =
    this.IFFShipmentCombineLatest(this.HHGShipmentSummaryRequest, this.HHGShipmentPaginationRequest);
  HHGShipmentResults$ =
    this.AFShipmentCombineLatest(this.HHGShipmentSummaryRequest, this.HHGShipmentPaginationRequest);

  SITShipmentResults$ = this.IFFShipmentCombineLatest(this.SITShipmentSummaryRequest, this.SITShipmentPaginationRequest);
  FRTShipmentResults$ = this.IFFShipmentCombineLatest(this.FRTShipmentSummaryRequest, this.FRTShipmentPaginationRequest);

  selectedHHGShipmentSummary$ = this.SelectedHHGShipmentSummary.asObservable();
  selectedFRTShipmentSummary$ = this.SelectedFRTShipmentSummary.asObservable();

  constructor(http: HttpClient, logger: LoggerService, private carrierService: HhgCarrierRateFilingService) {
    super(http);
    this._logger = logger;
  }

  updateHHGShipmentPaginationRequest = (pageSize: number,
                                        pageNumber: number,
                                        filterModel: any,
                                        sortModel: any) =>
    this.updateIFFShipmentSummaryPaginationRequest(pageSize, pageNumber, filterModel, sortModel, this.HHGShipmentPaginationRequest)

  updateSITShipmentPaginationRequest = (pageSize: number,
                                        pageNumber: number,
                                        filterModel: any,
                                        sortModel: any) =>
    this.updateIFFShipmentSummaryPaginationRequest(pageSize, pageNumber, filterModel, sortModel, this.SITShipmentPaginationRequest)

  updateFRTShipmentPaginationRequest = (pageSize: number,
                                        pageNumber: number,
                                        filterModel: any,
                                        sortModel: any) =>
    this.updateIFFShipmentSummaryPaginationRequest(pageSize, pageNumber, filterModel, sortModel, this.FRTShipmentPaginationRequest)

  updateSelectedHHGShipmentSummary(SelectedHHGShipmentSummary: IFFShipmentSummaryResponse) {
    this.SelectedHHGShipmentSummary.next(SelectedHHGShipmentSummary);
  }

  updateSelectedFRTShipmentSummary(SelectedFRTShipmentSummary: IFFShipmentSummaryResponse) {
    this.SelectedFRTShipmentSummary.next(SelectedFRTShipmentSummary);
  }

  exportIFFShipmentSummary(isFRTIffShipment: boolean): Observable<DownloadFileResponse> {
    const businessType = isFRTIffShipment ? 'FRT' : 'HHG';
    const iffShipmentRequest = new PageRequest(null, null, null, null, new IFFShipmentSummaryRequest(businessType, ''));
    return this.http.post<DownloadFileResponse>(this.iffMasterXlsxURI, iffShipmentRequest);
  }

  /*   deleteIffShipment(iffMasterId: any): Observable<any> {
      this.setUri(this.baseURI + '/deleteiffshipment');
      return this.update(iffMasterId);
    } */

  // 1539
  sendTestEmail() {
    const iffSendEmail = this.apiUri + '/public/iffMasterList/sendEmail';
    this.setUri(iffSendEmail);
    return this.get();
  }

  // 1539-end

  deleteIffShipment(iffMasterIds: number[]): Observable<any> {
    this.setUri(this.apiUri + '/iffMasterList/deleteiffshipment');
    return this.update(iffMasterIds);
  }

  // Get summary details for FRT, HHG, SIT summary request with server side pagination
  private getIFFShipmentSummary(
    IFFShipmentPaginationRequest: PageRequest<IFFShipmentSummaryRequest>
  ): Observable<PageResponse<IFFShipmentSummaryResponse>> {
    return this.http
      .post<PageResponse<IFFShipmentSummaryResponse>>(
        this.baseURI,
        IFFShipmentPaginationRequest
      )
      .pipe(
        catchError(e => {
          return of({content: [], totalElements: 0} as PageResponse<IFFShipmentSummaryResponse>);
        }));
  }

  private getAFShipmentSummary(
    IFFShipmentPaginationRequest: PageRequest<IFFShipmentSummaryRequest>, carrierId
  ): Observable<PageResponse<IFFShipmentSummaryResponse>> {
    return this.http
      .post<PageResponse<IFFShipmentSummaryResponse>>(
        this.baseURI + '/carrierId/' + carrierId,
        IFFShipmentPaginationRequest
      )
      .pipe(
        catchError(e => {
          return of({content: [], totalElements: 0} as PageResponse<IFFShipmentSummaryResponse>);
        }));
  }

  // Listens to all summary requests with pagination
  private IFFShipmentCombineLatest(IFFSummaryRequest: BehaviorSubject<IFFShipmentSummaryRequest>,
                                   IFFPaginationRequest: Subject<PageRequest<IFFShipmentSummaryRequest>>): Observable<PageResponse<IFFShipmentSummaryResponse>> {
    return combineLatest([IFFSummaryRequest,
        IFFPaginationRequest
      ]
    ).pipe(
      debounceTime(300),
      switchMap(([ShipmentSummaryRequest, ShipmentPaginationRequest]) => {
        ShipmentPaginationRequest.entityRequest = ShipmentSummaryRequest;
        return this.getIFFShipmentSummary(ShipmentPaginationRequest);
      }),
      catchError(e => {
        return of({content: [], totalElements: 0} as PageResponse<IFFShipmentSummaryResponse>);
      })
    );
  }

  private AFShipmentCombineLatest(IFFSummaryRequest: BehaviorSubject<IFFShipmentSummaryRequest>,
                                   IFFPaginationRequest: Subject<PageRequest<IFFShipmentSummaryRequest>>): Observable<PageResponse<IFFShipmentSummaryResponse>> {
    return combineLatest([IFFSummaryRequest,
        IFFPaginationRequest,
      this.carrierService.getCarrierIdByUser(),
      ]
    ).pipe(
      debounceTime(300),
      switchMap(([ShipmentSummaryRequest, ShipmentPaginationRequest, carrierObj]) => {
        ShipmentPaginationRequest.entityRequest = ShipmentSummaryRequest;
        const carrierId = carrierObj;
        return this.getAFShipmentSummary(ShipmentPaginationRequest, carrierId);
      }),
      catchError(e => {
        return of({content: [], totalElements: 0} as PageResponse<IFFShipmentSummaryResponse>);
      })
    );
  }

  // Common method to update pagination request and emits value with given subject.
  private updateIFFShipmentSummaryPaginationRequest(pageSize: number, pageNumber: number, filterModel: any, sortModel: any,
                                                    IFFShipmentPaginationRequest: Subject<PageRequest<IFFShipmentSummaryRequest>>) {
    IFFShipmentPaginationRequest.next(new PageRequest(
      pageSize,
      pageNumber,
      this.MapFilterCollection(filterModel),
      this.MapSortCollection(sortModel)
    ));
  }

  // map ag grid filter collection to pagination object
  private MapFilterCollection(filterModel: any): PageFilter[] {
    return [...Object.entries(filterModel)].map(
      filter =>
        new PageFilter(
          filter[0],
          filter[1]['filter'],
          filter[1]['type'],
          filter[1]['filterTo']
        )
    );
  }

  // map ag grid sort collection to pagination object
  private MapSortCollection(sortModel: any): PageSort[] {
    return sortModel.map(
      (s: { colId: string; sort: string }) => new PageSort(s.colId, s.sort)
    );
  }

  /**
   * @description adds % symbol at the end of the value
   */
  percentageFormatter(params) {
    return params.value + '%';
  }

  /**
   * @description formats the value into comma seperator number (in thousands place) and adds $ sign to it.
   */
  formatNumber(params) {
    const number = params.value;
    if (AppConstants.isNotNullCondition(params.value)) {
      const parts = params.value.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts.join('.');
    } else {
      return '';
    }
    // return '$' + Math.floor(number).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  }

  getPaygovToken1(totalAmount: BigInteger) {
    return this.http.get(this.paygovURI + totalAmount, { responseType: 'text' });
  }

  getPaygovToken(payload: any) {
    return this.http.post(this.paygovURI, payload, { responseType: 'text' });
  }
  completePaygovTransaction(token: string) {
    return this.http.get(this.paygovCompleteURI + token, { responseType: 'text' });
  }

}


